import { deserify, IStyles, PrimaryButton, pxToRem, pxTovW } from '@geneo2-web/shared-ui';
import { SessionStatusEnum } from '@protos/learning_management/lms.db_pb';
import { Box, Dialog, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';
import { HOME } from '../../../routeHandling/RoutesNomenclature';
import { resetLiveQuiz } from '../reducer/liveQuiz.slice';

// Same shell as the shared ContactPopup (libs/shared-ui .../Sidebar/ContactPopup)
// — white body, a circular icon badge overlapping the top edge via negative
// margin, close X top-right, heading, content divided by a bottom-border row
// — rather than the bespoke gradient-header card this used before.
const styles: IStyles = {
  iconRow: {
    marginTop: { xs: pxToRem(-45), md: pxTovW(-45) },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconWrapper: {
    backgroundColor: 'common.white',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: { xs: pxToRem(20), md: pxTovW(20) },
    borderRadius: '50%',
  },
  content: {
    px: { xs: pxToRem(30), md: pxTovW(40) },
    pb: { xs: pxToRem(30), md: pxTovW(40) },
    textAlign: 'center',
  },
  messageRow: {
    py: { xs: pxToRem(16), md: pxTovW(20) },
    mb: { xs: pxToRem(16), md: pxTovW(20) },
    borderBottom: '1px solid #D2D2D2',
  },
};

// Renders wherever the student happens to be (Waiting Room, mid-question,
// mid-reveal) — the moment session_status flips to COMPLETED this Dialog's
// backdrop covers everything else on screen, and "Go to Home" (or the close
// X, which does the same thing) is the only way out — no backdrop/Escape
// dismiss, no other section underneath it to interact with, since the
// teacher ending the quiz is a hard stop, not something to leave the student
// to dismiss and keep poking at a now-dead session.
export const LiveQuizEndedDialog = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { session_status, final_metrics } = deserify(useAppSelector((state) => state.liveQuiz));

  const open = session_status === SessionStatusEnum.SESSION_STATUS_COMPLETED;

  const handleGoHome = () => {
    dispatch(resetLiveQuiz());
    navigate(HOME);
  };

  const scorePercent =
    final_metrics?.maxScore != null && final_metrics.maxScore > 0
      ? Math.round((final_metrics.responseScore / final_metrics.maxScore) * 100)
      : undefined;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
          width: { xs: 295, md: 380 },
          m: 2,
          overflow: 'visible',
          position: 'relative',
        },
      }}
    >
      <CloseIcon
        sx={{
          color: 'common.black',
          fontSize: { xs: pxToRem(18), md: pxTovW(22) },
          position: 'absolute',
          top: { xs: pxToRem(10), md: pxTovW(10) },
          right: { xs: pxToRem(10), md: pxTovW(10) },
          cursor: 'pointer',
        }}
        onClick={handleGoHome}
      />

      <Box sx={styles.iconRow}>
        <Box sx={styles.iconWrapper}>
          <EmojiEventsIcon sx={{ fontSize: { xs: pxToRem(34), md: pxTovW(34) }, color: '#7C4DFF' }} />
        </Box>
      </Box>

      <Box sx={styles.content}>
        <Typography variant="h1">Quiz Ended</Typography>

        <Box sx={styles.messageRow}>
          <Typography variant="bodyText" color="text.secondary">
            Your teacher has ended the live quiz.
          </Typography>
        </Box>

        {final_metrics ? (
          <>
            <Typography variant="h3" fontWeight="bold" sx={{ color: '#7C4DFF' }}>
              {final_metrics.responseScore} / {final_metrics.maxScore}
              {scorePercent !== undefined ? ` (${scorePercent}%)` : ''}
            </Typography>
            <Typography variant="bodyText" color="text.secondary" sx={{ mb: { xs: pxToRem(24), md: pxTovW(24) } }}>
              {final_metrics.correctCount} of {final_metrics.totalQuestions} questions correct
            </Typography>
          </>
        ) : (
          <Typography variant="bodyText" color="text.secondary" sx={{ mb: { xs: pxToRem(24), md: pxTovW(24) } }}>
            Fetching your score…
          </Typography>
        )}

        <PrimaryButton fullWidth onClick={handleGoHome}>
          <Typography variant="h4" color="common.white" fontWeight="bold">
            Go to Home
          </Typography>
        </PrimaryButton>
      </Box>
    </Dialog>
  );
};

export default LiveQuizEndedDialog;
